
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MobileNavbar } from "./MobileNavbar"; 
import { MobileMenu } from "./MobileMenu";
import { MobileBottomNav } from "./MobileBottomNav";
import { MobileRoutes } from "./MobileRoutes";
import { gsap } from "gsap";
import { useToast } from "@/components/ui/use-toast";
import { CustomerTable } from "@/components/CustomerTable";

export function MobileLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [pageTitle, setPageTitle] = useState("Solucionar Soldas");
  const [isLoading, setIsLoading] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showCustomerTable, setShowCustomerTable] = useState(false);
  const location = useLocation();
  const { toast } = useToast();
  
  // Set page title based on route
  useEffect(() => {
    let title = "Solucionar Soldas";
    
    if (location.pathname.includes("dashboard")) {
      title = "Dashboard";
    } else if (location.pathname.includes("quotations")) {
      title = "Orçamentos";
    } else if (location.pathname.includes("stock")) {
      title = "Estoque";
    } else if (location.pathname.includes("reports")) {
      title = "Relatórios";
    } else if (location.pathname.includes("settings")) {
      title = "Configurações";
    }
    
    setPageTitle(title);
  }, [location]);
  
  // Check if location state has showCustomerTable flag
  useEffect(() => {
    if (location.state && location.state.showCustomerTable) {
      setShowCustomerTable(true);
      // Clear the state to prevent showing on page refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Check if app is installed
  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
  }, []);
  
  // Capture install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);
  
  // Page transition animation
  useEffect(() => {
    // Animate content on route change
    gsap.fromTo(
      "#page-content",
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }
    );
  }, [location.pathname]);
  
  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    // Show install prompt
    deferredPrompt.prompt();
    
    // Wait for user choice
    const choiceResult = await deferredPrompt.userChoice;
    
    if (choiceResult.outcome === 'accepted') {
      toast({
        title: "Instalação iniciada",
        description: "O aplicativo está sendo instalado no seu dispositivo",
      });
      setIsInstalled(true);
    }
    
    // Clear the deferredPrompt
    setDeferredPrompt(null);
  };

  const handleCloseCustomerTable = () => {
    setShowCustomerTable(false);
  };

  return (
    <div className="min-h-screen bg-[#1A1F2C]">
      <MobileNavbar 
        title={showCustomerTable ? "Clientes" : pageTitle}
        loading={isLoading}
        onMenuClick={() => setMenuOpen(true)}
        showInstallButton={!!deferredPrompt && !isInstalled}
        onInstallClick={handleInstallClick}
      />
      
      <MobileMenu 
        isOpen={menuOpen} 
        onClose={() => setMenuOpen(false)} 
      />
      
      <main 
        id="page-content"
        className="pt-16 pb-16 min-h-screen"
      >
        {showCustomerTable ? (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Gerenciamento de Clientes</h2>
              <button
                onClick={handleCloseCustomerTable}
                className="text-white p-2 rounded-full hover:bg-white/10"
              >
                ✕
              </button>
            </div>
            <CustomerTable />
          </div>
        ) : (
          <MobileRoutes />
        )}
      </main>
      
      <MobileBottomNav />
    </div>
  );
}
